import { ConsentPlugin } from './plugins';
import {
  ConsentEvent,
  ConsentMediatorType,
  ConsentState,
  ConsentStateObserverFunction,
} from './types';
import { LoggerSkeleton } from '../types';

class ConsentMediator implements ConsentMediatorType {
  #consentPlugin: ConsentPlugin<ConsentEvent, ConsentState>;
  #observers: Map<ConsentEvent, ConsentStateObserverFunction[] | undefined>;
  #isInitialized = false;
  #logger: LoggerSkeleton;

  constructor(consentPlugin: ConsentPlugin<ConsentEvent, ConsentState>, logger: LoggerSkeleton) {
    this.#consentPlugin = consentPlugin;
    this.#observers = new Map<ConsentEvent, ConsentStateObserverFunction[] | undefined>();
    this.#logger = logger;
  }

  initialize() {
    if (!this.#isInitialized) {
      try {
        this.#consentPlugin.initialize();
        this.#consentPlugin.subscribe(
          ConsentEvent.CONSENT_STATE_CHANGE,
          this._onConsentStateChange.bind(this)
        );
      } catch (e: unknown) {
        this.#isInitialized = false;
        this.#logger.error('class=ConsentMediator | #initialize', {}, e);
      }
      this.#isInitialized = true;
    }
  }

  public getConsentState(): ConsentState {
    return this.#consentPlugin.getConsentState();
  }

  /**
   * For testing and internal use only!
   * @param consentState
   */
  _onConsentStateChange(consentState: ConsentState) {
    this.#logger.debug('class=ConsentMediator | #_onConsentStateChange', {
      consentState,
    });
    this.notify(ConsentEvent.CONSENT_STATE_CHANGE, consentState);
  }

  notify(event: ConsentEvent, data: ConsentState): void {
    this.#logger.debug('class=ConsentMediator | #notify', {
      event,
      data,
    });
    const observers: ConsentStateObserverFunction[] | undefined = this.#observers.get(event);
    observers?.forEach(observer => observer(data));
  }

  subscribe(event: ConsentEvent, observer: ConsentStateObserverFunction): void {
    this.#logger.debug('class=ConsentMediator | #subscribe', {
      event,
      observer,
    });
    const observers: ConsentStateObserverFunction[] | undefined = this.#observers.get(event) || [
      observer,
    ];
    this.#observers.set(event, observers);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  unsubscribe(event: ConsentEvent, observer: ConsentStateObserverFunction): void {
    // TODO: implement me!
  }
}

export default ConsentMediator;
